import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { getPublication, deleteTopic, reorderTopics } from "../../api/publicationsApi";
import { translateAuthError } from "../../api/authApi";
import { CATEGORY_ORDER } from "../../data/publications";

// Section C (admin-only): manages the cards (topics) of one publication,
// grouped under its six fixed categories. This is where "create new
// cards in each category and delete them" happens.
function PublicationTopicsPage() {
  const { slug } = useParams();
  const [publication, setPublication] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem("token");

  function load() {
    setLoading(true);
    getPublication(slug)
      .then((pub) => {
        const topicsByCategory = {};
        for (const t of pub.topics) {
          (topicsByCategory[t.category] ??= []).push(t);
        }
        setPublication({ ...pub, topics: topicsByCategory });
      })
      .catch((err) => setError(translateAuthError(err.message)))
      .finally(() => setLoading(false));
  }

  useEffect(load, [slug]);

  async function handleDeleteTopic(topicId) {
    if (!window.confirm("این کارت حذف شود؟")) return;
    try {
      await deleteTopic(token, topicId);
      setPublication((prev) => {
        const topics = {};
        for (const category of Object.keys(prev.topics)) {
          topics[category] = prev.topics[category].filter((t) => t.id !== topicId);
        }
        return { ...prev, topics };
      });
    } catch (err) {
      setError(translateAuthError(err.message));
    }
  }

  // Swaps the card at `index` with its neighbor in `direction` (-1 up /
  // +1 down) within one category section, then submits that section's
  // full new id order to the backend. Applied to local state immediately
  // (the swap is purely a reorder, so there's nothing to "fail" visually)
  // and rolled back only if the request itself fails.
  async function moveTopic(category, index, direction) {
    const current = publication.topics[category] || [];
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= current.length) return;

    const reordered = [...current];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];

    setPublication((prev) => ({ ...prev, topics: { ...prev.topics, [category]: reordered } }));

    try {
      await reorderTopics(token, slug, category, reordered.map((t) => t.id));
    } catch (err) {
      setError(translateAuthError(err.message));
      setPublication((prev) => ({ ...prev, topics: { ...prev.topics, [category]: current } }));
    }
  }

  return (
    <div className="admin-page">
      <Link to="/admin/publications" className="back-link">
        ← بازگشت به فهرست نشریه‌ها
      </Link>

      {error && <p className="error">{error}</p>}

      {loading ? (
        <p>در حال بارگذاری...</p>
      ) : (
        publication && (
          <>
            <div className="admin-page-header">
              <h1>کارت‌های «{publication.title}»</h1>
            </div>

            {CATEGORY_ORDER.map((category) => (
              <div className="admin-topic-section" key={category}>
                <h2>{category}</h2>

                {(publication.topics[category] || []).length === 0 && (
                  <p className="admin-empty">کارتی در این دسته نیست.</p>
                )}

                <ul className="admin-article-list">
                  {(publication.topics[category] || []).map((t, index) => (
                    <li key={t.id} className="admin-article-row">
                      <div className="admin-reorder-buttons">
                        <button
                          type="button"
                          className="admin-reorder-button"
                          disabled={index === 0}
                          onClick={() => moveTopic(category, index, -1)}
                          aria-label="انتقال به بالا"
                        >
                          ▲
                        </button>
                        <button
                          type="button"
                          className="admin-reorder-button"
                          disabled={index === (publication.topics[category] || []).length - 1}
                          onClick={() => moveTopic(category, index, 1)}
                          aria-label="انتقال به پایین"
                        >
                          ▼
                        </button>
                      </div>
                      <span className="admin-article-row-title">{t.title_fa || t.title_en}</span>
                      <div className="admin-actions">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/publications/${slug}/topics/${t.id}/edit`)}
                        >
                          ویرایش
                        </button>
                        <button type="button" onClick={() => handleDeleteTopic(t.id)}>
                          حذف
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>

                <Link
                  to={`/admin/publications/${slug}/topics/new?category=${encodeURIComponent(category)}`}
                  className="nav-cta"
                >
                  افزودن کارت
                </Link>
              </div>
            ))}
          </>
        )
      )}
    </div>
  );
}

export default PublicationTopicsPage;
